import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { ClipboardDocumentIcon, ExclamationTriangleIcon, CheckIcon } from '@heroicons/react/24/outline';
import { S3AccessKeySecret } from '../../types/api';

interface RevealSecretOnceModalProps {
  secret: S3AccessKeySecret;
  onClose: () => void;
}

/**
 * Modal CRITIQUE — affiche le secret B2 UNE SEULE FOIS au moment de la création.
 * NubieCloud ne stocke pas le secret côté serveur. Si le user le perd, il doit
 * révoquer + créer une nouvelle clé.
 */
const RevealSecretOnceModal: React.FC<RevealSecretOnceModalProps> = ({ secret, onClose }) => {
  const { t } = (useTranslation as any)('nubis3');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast.error('Clipboard access denied');
    }
  };

  const envBlock = `# NubiS3 / Backblaze B2
AWS_ACCESS_KEY_ID=${secret.b2_key_id}
AWS_SECRET_ACCESS_KEY=${secret.application_key}
AWS_ENDPOINT_URL=${secret.endpoint}
AWS_REGION=${secret.region}
S3_BUCKET=${secret.bucket_name}
`;

  const fields: { label: string; value: string; key: string }[] = [
    { label: t('accessKeys.reveal.endpoint'), value: secret.endpoint, key: 'endpoint' },
    { label: t('accessKeys.reveal.bucketName'), value: secret.bucket_name, key: 'bucket' },
    { label: t('accessKeys.reveal.region'), value: secret.region, key: 'region' },
    { label: t('accessKeys.reveal.keyId'), value: secret.b2_key_id, key: 'keyId' },
    { label: t('accessKeys.reveal.secret'), value: secret.application_key, key: 'secret' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
      <div className="bg-surface-1 rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-1">
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <CheckIcon className="w-5 h-5 text-green-500" />
            {t('accessKeys.reveal.title')}
          </h2>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Warning bandeau */}
          <div className="flex items-start gap-3 p-4 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">{t('accessKeys.reveal.warning')}</p>
          </div>

          {/* Champs individuels */}
          <div className="space-y-3">
            {fields.map(({ label, value, key }) => (
              <div key={key}>
                <label className="block text-xs font-medium text-text-secondary mb-1">{label}</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-md bg-surface-2 border border-border-1 text-sm text-text-primary font-mono break-all">
                    {value}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(value, key)}
                    className="px-3 py-2 rounded-md bg-surface-2 hover:bg-surface-3 border border-border-1 text-xs font-medium text-text-primary flex items-center gap-1.5"
                  >
                    {copiedField === key ? (
                      <><CheckIcon className="w-4 h-4 text-green-500" /> {t('accessKeys.reveal.copied')}</>
                    ) : (
                      <><ClipboardDocumentIcon className="w-4 h-4" /> {t('accessKeys.reveal.copy')}</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Bloc .env complet */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-text-secondary">
                {t('accessKeys.reveal.envExample')}
              </label>
              <button
                type="button"
                onClick={() => copyToClipboard(envBlock, 'env')}
                className="px-2 py-1 rounded text-xs font-medium bg-surface-2 hover:bg-surface-3 border border-border-1 text-text-primary flex items-center gap-1"
              >
                {copiedField === 'env' ? (
                  <><CheckIcon className="w-3 h-3 text-green-500" /> {t('accessKeys.reveal.copied')}</>
                ) : (
                  <><ClipboardDocumentIcon className="w-3 h-3" /> {t('accessKeys.reveal.envCopy')}</>
                )}
              </button>
            </div>
            <pre className="px-3 py-2 rounded-md bg-surface-2 border border-border-1 text-xs text-text-primary font-mono overflow-x-auto whitespace-pre">
              {envBlock}
            </pre>
          </div>
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-border-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium bg-accent-500 text-white hover:bg-accent-600"
          >
            {t('accessKeys.reveal.done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevealSecretOnceModal;
